import { useState, useEffect } from 'react'
import { Header } from '../components/layout/Header'

interface PPCCampaign {
  id: string
  name: string
  platform: string
  budget: number
  spent: number
  impressions: number
  clicks: number
  conversions: number
  cpc: number
  cpa: number
  roas: number
  quality_score: number
}

interface KeywordPerformance {
  id: string
  keyword: string
  match_type: string
  impressions: number
  clicks: number
  ctr: number
  cpc: number
  conversions: number
  cvr: number
}

interface Statistics {
  total_spend: number
  total_conversions: number
  avg_cpc: number
  avg_cpa: number
  overall_roas: number
  impression_share: number
}

export default function PPCAnalyticsPage() {
  const [campaigns, setCampaigns] = useState<PPCCampaign[]>([])
  const [keywords, setKeywords] = useState<KeywordPerformance[]>([])
  const [statistics, setStatistics] = useState<Statistics | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    fetchData()
  }, [])

  const fetchData = async () => {
    try {
      const [campaignsRes, keywordsRes, statsRes] = await Promise.all([
        fetch('/api/ppcanalytics/campaigns'),
        fetch('/api/ppcanalytics/keywords'),
        fetch('/api/ppcanalytics/statistics')
      ])
      if (campaignsRes.ok) setCampaigns(await campaignsRes.json())
      if (keywordsRes.ok) setKeywords(await keywordsRes.json())
      if (statsRes.ok) setStatistics(await statsRes.json())
    } catch (error) {
      console.error('Error fetching PPC analytics data:', error)
    } finally {
      setLoading(false)
    }
  }

  const formatNumber = (value: number) => {
    return new Intl.NumberFormat('en-US').format(value)
  }

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 2 }).format(value)
  }

  const getPlatformColor = (platform: string) => {
    const colors: { [key: string]: string } = {
      'Google Ads': 'bg-blue-100 text-blue-800',
      'LinkedIn': 'bg-indigo-100 text-indigo-800',
      'Meta': 'bg-blue-100 text-blue-800'
    }
    return colors[platform] || 'bg-gray-100 text-gray-800'
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">PPC Analytics</h1>
          <p className="mt-2 text-gray-600">Phase 248 - Paid advertising performance</p>
        </div>

        {statistics && (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Total Spend</p>
              <p className="text-2xl font-bold text-gray-900">{formatCurrency(statistics.total_spend)}</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Conversions</p>
              <p className="text-2xl font-bold text-green-600">{formatNumber(statistics.total_conversions)}</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Avg CPC</p>
              <p className="text-2xl font-bold text-blue-600">{formatCurrency(statistics.avg_cpc)}</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Avg CPA</p>
              <p className="text-2xl font-bold text-purple-600">{formatCurrency(statistics.avg_cpa)}</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">ROAS</p>
              <p className="text-2xl font-bold text-green-600">{statistics.overall_roas}x</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Impression Share</p>
              <p className="text-2xl font-bold text-orange-600">{statistics.impression_share}%</p>
            </div>
          </div>
        )}

        <div className="bg-white rounded-lg shadow mb-6">
          <div className="px-6 py-4 border-b">
            <h2 className="text-lg font-semibold">PPC Campaigns</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Campaign</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Platform</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Spent</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Clicks</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">CPC</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Conv</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">ROAS</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">QS</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {campaigns.map((campaign) => (
                  <tr key={campaign.id}>
                    <td className="px-6 py-4 font-medium text-gray-900">{campaign.name}</td>
                    <td className="px-6 py-4">
                      <span className={`px-2 py-1 text-xs rounded-full ${getPlatformColor(campaign.platform)}`}>{campaign.platform}</span>
                    </td>
                    <td className="px-6 py-4 text-gray-500">{formatCurrency(campaign.spent)}</td>
                    <td className="px-6 py-4 text-blue-600">{formatNumber(campaign.clicks)}</td>
                    <td className="px-6 py-4 text-gray-500">{formatCurrency(campaign.cpc)}</td>
                    <td className="px-6 py-4 text-green-600 font-medium">{formatNumber(campaign.conversions)}</td>
                    <td className="px-6 py-4 text-purple-600 font-bold">{campaign.roas}x</td>
                    <td className="px-6 py-4">
                      <span className={`px-2 py-1 text-xs rounded-full ${campaign.quality_score >= 7 ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`}>{campaign.quality_score}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow">
          <div className="px-6 py-4 border-b">
            <h2 className="text-lg font-semibold">Keyword Performance</h2>
          </div>
          <div className="p-6 space-y-4">
            {keywords.map((keyword) => (
              <div key={keyword.id} className="p-4 bg-gray-50 rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <div>
                    <span className="font-medium text-gray-900">{keyword.keyword}</span>
                    <span className="ml-2 px-2 py-1 text-xs bg-gray-100 text-gray-600 rounded">{keyword.match_type}</span>
                  </div>
                  <span className="text-lg font-bold text-green-600">{keyword.cvr}% CVR</span>
                </div>
                <div className="grid grid-cols-5 gap-2 text-sm">
                  <div className="text-center p-2 bg-white rounded">
                    <p className="text-gray-500">Impressions</p>
                    <p className="font-medium">{formatNumber(keyword.impressions)}</p>
                  </div>
                  <div className="text-center p-2 bg-white rounded">
                    <p className="text-gray-500">Clicks</p>
                    <p className="font-medium text-blue-600">{formatNumber(keyword.clicks)}</p>
                  </div>
                  <div className="text-center p-2 bg-white rounded">
                    <p className="text-gray-500">CTR</p>
                    <p className="font-medium text-purple-600">{keyword.ctr}%</p>
                  </div>
                  <div className="text-center p-2 bg-white rounded">
                    <p className="text-gray-500">CPC</p>
                    <p className="font-medium text-orange-600">{formatCurrency(keyword.cpc)}</p>
                  </div>
                  <div className="text-center p-2 bg-white rounded">
                    <p className="text-gray-500">Conversions</p>
                    <p className="font-medium text-green-600">{keyword.conversions}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </main>
    </div>
  )
}
